import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  HardHat, LogOut, Users, FileText, CheckCircle, XCircle,
  Clock, Shield, Eye, ChevronRight, AlertCircle, BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [verificationQueue, setVerificationQueue] = useState([]);
  const [activeTab, setActiveTab] = useState('verification');
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, queueRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/verification-queue`)
      ]);
      setStats(statsRes.data);
      setVerificationQueue(queueRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyLicense = async (contractorId, approved) => {
    setProcessingId(`license-${contractorId}`);
    try {
      await axios.post(`${API}/admin/verify-license/${contractorId}?approved=${approved}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to verify');
    } finally {
      setProcessingId(null);
    }
  };

  const handleVerifyInsurance = async (contractorId, approved) => {
    setProcessingId(`insurance-${contractorId}`);
    try {
      await axios.post(`${API}/admin/verify-insurance/${contractorId}?approved=${approved}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to verify');
    } finally {
      setProcessingId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight">ADMIN PANEL</span>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-white text-sm">{user?.email}</span>
              <button 
                onClick={handleLogout}
                className="text-slate-400 hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500 uppercase font-bold">Clients</span>
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-black text-slate-900">{stats?.total_clients || 0}</p>
          </div>
          
          <div className="bg-white rounded-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500 uppercase font-bold">Contractors</span>
              <HardHat className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-3xl font-black text-slate-900">{stats?.total_contractors || 0}</p>
          </div>
          
          <div className="bg-white rounded-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500 uppercase font-bold">Total Jobs</span>
              <FileText className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-3xl font-black text-slate-900">{stats?.total_jobs || 0}</p>
          </div>
          
          <div className="bg-white rounded-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500 uppercase font-bold">Active Jobs</span>
              <Clock className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-black text-slate-900">{stats?.active_jobs || 0}</p>
          </div>
          
          <div className="bg-orange-600 rounded-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-white/80 uppercase font-bold">Pending</span>
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <p className="text-3xl font-black text-white">{stats?.pending_verifications || 0}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['verification', 'contractors', 'jobs'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-sm font-bold text-sm uppercase whitespace-nowrap transition-colors ${
                activeTab === tab
                  ? 'bg-orange-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab === 'verification' ? 'Verification Queue' : tab}
            </button>
          ))}
        </div>

        {/* Verification Queue */}
        {activeTab === 'verification' && (
          <div className="bg-white rounded-sm border border-slate-200">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-slate-900">Pending Verifications</h3>
              <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-sm font-bold">
                {verificationQueue.length} pending
              </span>
            </div>
            <div className="divide-y divide-slate-100">
              {verificationQueue.length > 0 ? (
                verificationQueue.map((contractor) => (
                  <div key={contractor.id} className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-bold text-slate-900">{contractor.business_name}</h4>
                        <p className="text-sm text-slate-500">
                          {contractor.user?.first_name} {contractor.user?.last_name} • {contractor.user?.email}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          <span>Zip: {contractor.service_zip}</span>
                          <span>Radius: {contractor.service_radius_miles} mi</span>
                          <span>Categories: {contractor.categories?.join(', ')}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* License Verification */}
                    <div className="bg-slate-50 rounded p-4 mb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            contractor.license_verified === 'approved' ? 'bg-green-100' :
                            contractor.license_verified === 'rejected' ? 'bg-red-100' : 'bg-yellow-100'
                          }`}>
                            {contractor.license_verified === 'approved' ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : contractor.license_verified === 'rejected' ? (
                              <XCircle className="w-4 h-4 text-red-600" />
                            ) : (
                              <Clock className="w-4 h-4 text-yellow-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">License Document</p>
                            <p className="text-xs text-slate-500">
                              {contractor.license_number ? `#${contractor.license_number}` : 'No license number provided'}
                            </p>
                          </div>
                        </div>
                        
                        {contractor.license_verified === 'pending' && contractor.license_document_url && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleVerifyLicense(contractor.user_id, true)}
                              disabled={processingId === `license-${contractor.user_id}`}
                              className="bg-green-600 text-white px-3 py-1 rounded text-sm font-bold flex items-center gap-1 disabled:opacity-50"
                              data-testid={`approve-license-${contractor.user_id}`}
                            >
                              <CheckCircle className="w-4 h-4" /> Approve
                            </button>
                            <button
                              onClick={() => handleVerifyLicense(contractor.user_id, false)}
                              disabled={processingId === `license-${contractor.user_id}`}
                              className="bg-red-600 text-white px-3 py-1 rounded text-sm font-bold flex items-center gap-1 disabled:opacity-50"
                              data-testid={`reject-license-${contractor.user_id}`}
                            >
                              <XCircle className="w-4 h-4" /> Reject
                            </button>
                          </div>
                        )}
                        
                        {contractor.license_verified === 'pending' && !contractor.license_document_url && (
                          <span className="text-sm text-slate-500">No document uploaded</span>
                        )}
                        
                        {contractor.license_verified === 'approved' && (
                          <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold">Approved</span>
                        )}
                        
                        {contractor.license_verified === 'rejected' && (
                          <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">Rejected</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Insurance Verification */}
                    <div className="bg-slate-50 rounded p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            contractor.insurance_verified === 'approved' ? 'bg-green-100' :
                            contractor.insurance_verified === 'rejected' ? 'bg-red-100' : 'bg-yellow-100'
                          }`}>
                            {contractor.insurance_verified === 'approved' ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : contractor.insurance_verified === 'rejected' ? (
                              <XCircle className="w-4 h-4 text-red-600" />
                            ) : (
                              <Clock className="w-4 h-4 text-yellow-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">Insurance Certificate</p>
                            <p className="text-xs text-slate-500">General liability insurance</p>
                          </div>
                        </div>
                        
                        {contractor.insurance_verified === 'pending' && contractor.insurance_document_url && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleVerifyInsurance(contractor.user_id, true)}
                              disabled={processingId === `insurance-${contractor.user_id}`}
                              className="bg-green-600 text-white px-3 py-1 rounded text-sm font-bold flex items-center gap-1 disabled:opacity-50"
                              data-testid={`approve-insurance-${contractor.user_id}`}
                            >
                              <CheckCircle className="w-4 h-4" /> Approve
                            </button>
                            <button
                              onClick={() => handleVerifyInsurance(contractor.user_id, false)}
                              disabled={processingId === `insurance-${contractor.user_id}`}
                              className="bg-red-600 text-white px-3 py-1 rounded text-sm font-bold flex items-center gap-1 disabled:opacity-50"
                              data-testid={`reject-insurance-${contractor.user_id}`}
                            >
                              <XCircle className="w-4 h-4" /> Reject
                            </button>
                          </div>
                        )}
                        
                        {contractor.insurance_verified === 'pending' && !contractor.insurance_document_url && (
                          <span className="text-sm text-slate-500">No document uploaded</span>
                        )}
                        
                        {contractor.insurance_verified === 'approved' && (
                          <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold">Approved</span>
                        )}
                        
                        {contractor.insurance_verified === 'rejected' && (
                          <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">Rejected</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
                  <p className="text-slate-500">No pending verifications</p>
                  <p className="text-sm text-slate-400 mt-1">All contractors are verified!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Placeholder for other tabs */}
        {activeTab === 'contractors' && (
          <div className="bg-white rounded-sm border border-slate-200 p-8 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Contractor management coming soon</p>
          </div>
        )}

        {activeTab === 'jobs' && (
          <div className="bg-white rounded-sm border border-slate-200 p-8 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Job management coming soon</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
